/**
 * ProfileSwitcher.tsx - Profile Switcher Component
 * 
 * CHANGE HISTORY:
 * - 251102_1706 danielbeer: Create ProfileSwitcher component for Interfacr
 * 
 * Quick profile switching dropdown component
 */

import React, { useState } from 'react';
import type { Profile, ResolvedProfile } from '@opcentral/profylr';

export interface ProfileSwitcherProps {
  /** Current active profile */
  activeProfile: ResolvedProfile | null;
  
  /** Available profiles */
  profiles: Profile[];
  
  /** Callback when profile is activated */
  onActivate: (profileId: string) => Promise<void>;
  
  /** Show profile descriptions */
  showDescriptions?: boolean;
  
  /** Show profile tags */
  showTags?: boolean;
  
  /** Custom className */
  className?: string;
  
  /** Loading state */
  loading?: boolean;
}

/**
 * ProfileSwitcher - Quick profile switching dropdown
 * 
 * @example
 * ```tsx
 * <ProfileSwitcher
 *   activeProfile={activeProfile}
 *   profiles={profiles}
 *   onActivate={activate}
 *   showDescriptions
 * />
 * ```
 */
export const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({
  activeProfile,
  profiles,
  onActivate,
  showDescriptions = false,
  showTags = false,
  className = '',
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  
  const handleActivate = async (profileId: string) => {
    setSwitching(true);
    try {
      await onActivate(profileId);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to activate profile:', error);
    } finally {
      setSwitching(false);
    }
  };
  
  return (
    <div className={`profile-switcher ${className}`}>
      {/* Trigger Button */}
      <button
        className="profile-switcher-trigger"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading || switching}
      >
        <span className="profile-switcher-icon">👤</span>
        <span className="profile-switcher-label">
          {activeProfile?.name || 'No Profile'}
        </span>
        <span className="profile-switcher-arrow">
          {isOpen ? '▲' : '▼'}
        </span>
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="profile-switcher-dropdown">
          <div className="profile-switcher-header">
            <h3>Switch Profile</h3>
            <button
              className="profile-switcher-close"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>
          
          <div className="profile-switcher-list">
            {profiles.map((profile) => {
              const isActive = activeProfile?.id === profile.id;
              
              return (
                <button
                  key={profile.id}
                  className={`profile-switcher-item ${isActive ? 'active' : ''}`}
                  onClick={() => !isActive && handleActivate(profile.id)}
                  disabled={switching || isActive}
                >
                  <div className="profile-switcher-item-header">
                    <span className="profile-switcher-item-name">
                      {profile.name}
                    </span>
                    {isActive && (
                      <span className="profile-switcher-item-badge">Active</span>
                    )}
                  </div>
                  
                  {showDescriptions && profile.description && (
                    <p className="profile-switcher-item-description">
                      {profile.description}
                    </p>
                  )}
                  
                  {showTags && profile.tags && profile.tags.length > 0 && (
                    <div className="profile-switcher-item-tags">
                      {profile.tags.map((tag) => (
                        <span key={tag} className="profile-switcher-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {profiles.length === 0 && (
            <div className="profile-switcher-empty">
              <p>No profiles available</p>
            </div>
          )}
        </div>
      )}
      
      {/* Overlay */}
      {isOpen && (
        <div
          className="profile-switcher-overlay"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ProfileSwitcher;
